import { useComputed, useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import {
  IcoArchive,
  IcoChevronRight,
  IcoEye,
  IcoEyeOff,
  IcoPlus,
  IcoSpinner,
  IcoStar,
  IcoTrash,
} from "../../../components/icons";
import { BrandedLayout, ConfirmDialog } from "../../../components/ui";
import { usePageTitle } from "../../../hooks";
import { rpc } from "../../../rpc";
import { showError } from "../../../stores/errors";
import type { MimicState } from "../../../stores/mimic";
import {
  type BankQuestion,
  type BrandColor,
  isAdmin,
  type User,
} from "../../../types";
import { getErrorMessage } from "../../../utils/errors";

type QuestionBankPageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  hasReports: boolean;
  workspaceId: string;
  questions: BankQuestion[];
  categories: string[];
};

export async function load(args: LoaderArgs): Promise<QuestionBankPageState> {
  const orgId = args.params.orgId ?? "";

  const [authResult, orgsResult, orgResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const isOrgAdmin = isAdmin(orgResult.currentMember.role);
  if (!isOrgAdmin) {
    throw new RedirectError(`/orgs/${orgId}`);
  }

  const workspaceId = orgResult.organization.id;

  const [questionsResult, categoriesResult] = await Promise.all([
    rpc.questionBank.list({ workspaceId, includeArchived: true }),
    rpc.questionBank.getCategories({ workspaceId }),
  ]);

  // Get brand color from list endpoint
  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    hasReports: orgFromList?.hasReports ?? false,
    workspaceId,
    questions: questionsResult.questions,
    categories: categoriesResult.categories,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Question Bank");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    hasReports,
    workspaceId,
    questions,
  } = state.value;

  const questionList = useSignal(questions);
  const isCreating = useSignal(false);
  const showArchived = useSignal(false);
  const categoryFilter = useSignal<string | null>(null);
  const deleteTarget = useSignal<BankQuestion | null>(null);
  const isDeleting = useSignal(false);

  // Get unique categories from current questions
  const categories = useComputed(() => {
    const cats = new Set<string>();
    for (const q of questionList.value) {
      if (q.category && !q.archivedAt) {
        cats.add(q.category);
      }
    }
    return Array.from(cats).sort();
  });

  // Filter questions
  const activeQuestions = useComputed(() =>
    questionList.value.filter((q) => {
      if (q.archivedAt) return false;
      if (categoryFilter.value && q.category !== categoryFilter.value)
        return false;
      return true;
    }),
  );

  const archivedQuestions = useComputed(() =>
    questionList.value.filter((q) => q.archivedAt),
  );

  // Group active questions by category
  const groupedQuestions = useComputed(() => {
    const groups = new Map<string, BankQuestion[]>();
    const uncategorized: BankQuestion[] = [];

    for (const q of activeQuestions.value) {
      if (q.category) {
        const list = groups.get(q.category) ?? [];
        list.push(q);
        groups.set(q.category, list);
      } else {
        uncategorized.push(q);
      }
    }

    // Sort each group by createdAt descending
    for (const list of groups.values()) {
      list.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }
    uncategorized.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return { groups, uncategorized };
  });

  async function handleCreate() {
    if (isCreating.value) return;
    isCreating.value = true;

    try {
      const result = await rpc.questionBank.create({
        workspaceId,
        title: "New Question",
      });
      navigateTo(`/orgs/${orgId}/forms/bank/${result.question.id}`);
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isCreating.value = false;
    }
  }

  async function handleDelete() {
    const question = deleteTarget.value;
    if (!question || isDeleting.value) return;

    isDeleting.value = true;
    try {
      await rpc.questionBank.remove({ questionId: question.id });
      questionList.value = questionList.value.filter(
        (q) => q.id !== question.id,
      );
      deleteTarget.value = null;
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isDeleting.value = false;
    }
  }

  async function handleArchive(question: BankQuestion, archived: boolean) {
    try {
      await rpc.questionBank.archive({ questionId: question.id, archived });
      questionList.value = questionList.value.map((q) =>
        q.id === question.id
          ? { ...q, archivedAt: archived ? new Date() : null }
          : q,
      );
    } catch (err) {
      showError(getErrorMessage(err));
    }
  }

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="forms"
      isAdmin={true}
      hasReports={hasReports}
    >
      {/* Page header */}
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-text">Question Bank</h1>
          <p class="text-sm text-text-muted mt-1">
            Create reusable questions to quickly add to your forms
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={isCreating.value}
          class="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50"
        >
          {isCreating.value ? (
            <IcoSpinner class="size-4 animate-spin" />
          ) : (
            <IcoPlus class="size-4" />
          )}
          New Question
        </button>
      </div>

      {/* Category filter */}
      {categories.value.length > 0 && (
        <div class="flex items-center gap-2 mb-4 flex-wrap">
          <button
            type="button"
            onClick={() => {
              categoryFilter.value = null;
            }}
            class={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              categoryFilter.value === null
                ? "bg-surface-active text-text"
                : "bg-surface-hover text-text-muted hover:text-text"
            }`}
          >
            All
          </button>
          {categories.value.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => {
                categoryFilter.value = cat;
              }}
              class={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                categoryFilter.value === cat
                  ? "bg-surface-active text-text"
                  : "bg-surface-hover text-text-muted hover:text-text"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Questions list */}
      {activeQuestions.value.length === 0 &&
      archivedQuestions.value.length === 0 ? (
        <EmptyState
          onCreateClick={handleCreate}
          isCreating={isCreating.value}
        />
      ) : (
        <div class="flex flex-col gap-6">
          {/* Grouped by category */}
          {categoryFilter.value === null ? (
            <>
              {/* Uncategorized first */}
              {groupedQuestions.value.uncategorized.length > 0 && (
                <QuestionSection
                  title="Uncategorized"
                  questions={groupedQuestions.value.uncategorized}
                  orgId={orgId}
                  onArchive={handleArchive}
                  onDelete={(q) => {
                    deleteTarget.value = q;
                  }}
                />
              )}

              {/* Then by category */}
              {Array.from(groupedQuestions.value.groups.entries()).map(
                ([category, qs]) => (
                  <QuestionSection
                    key={category}
                    title={category}
                    questions={qs}
                    orgId={orgId}
                    onArchive={handleArchive}
                    onDelete={(q) => {
                      deleteTarget.value = q;
                    }}
                  />
                ),
              )}
            </>
          ) : (
            /* Filtered view - no grouping */
            <QuestionSection
              title={categoryFilter.value}
              questions={activeQuestions.value}
              orgId={orgId}
              onArchive={handleArchive}
              onDelete={(q) => {
                deleteTarget.value = q;
              }}
            />
          )}

          {/* Archived section */}
          {archivedQuestions.value.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => {
                  showArchived.value = !showArchived.value;
                }}
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
              >
                {showArchived.value ? (
                  <IcoEyeOff class="size-4" />
                ) : (
                  <IcoEye class="size-4" />
                )}
                {showArchived.value ? "Hide" : "Show"} archived (
                {archivedQuestions.value.length})
              </button>

              {showArchived.value && (
                <div class="mt-3">
                  <QuestionSection
                    title="Archived"
                    questions={archivedQuestions.value}
                    orgId={orgId}
                    isArchived
                    onArchive={handleArchive}
                    onDelete={(q) => {
                      deleteTarget.value = q;
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteTarget.value !== null}
        title="Delete Question"
        confirmLabel={isDeleting.value ? "Deleting..." : "Delete"}
        onConfirm={handleDelete}
        onCancel={() => {
          deleteTarget.value = null;
        }}
      >
        Are you sure you want to delete "{deleteTarget.value?.title}"? This
        action cannot be undone.
      </ConfirmDialog>
    </BrandedLayout>
  );
}

// ============================================================================
// Components
// ============================================================================

function EmptyState({
  onCreateClick,
  isCreating,
}: {
  onCreateClick: () => void;
  isCreating: boolean;
}) {
  return (
    <div class="rounded-xl border border-border bg-surface p-12 text-center">
      <div class="size-14 mx-auto mb-4 rounded-full bg-surface-muted flex items-center justify-center">
        <IcoStar class="size-7 text-text-muted" />
      </div>
      <h3 class="font-medium text-text mb-2">No questions yet</h3>
      <p class="text-text-muted mb-6 max-w-sm mx-auto">
        No questions in your bank yet. Create reusable questions to quickly add
        to your forms.
      </p>
      <button
        type="button"
        onClick={onCreateClick}
        disabled={isCreating}
        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50"
      >
        {isCreating ? (
          <IcoSpinner class="size-4 animate-spin" />
        ) : (
          <IcoPlus class="size-4" />
        )}
        Create your first question
      </button>
    </div>
  );
}

function QuestionSection({
  title,
  questions,
  orgId,
  isArchived,
  onArchive,
  onDelete,
}: {
  title: string;
  questions: BankQuestion[];
  orgId: string;
  isArchived?: boolean;
  onArchive: (q: BankQuestion, archived: boolean) => void;
  onDelete: (q: BankQuestion) => void;
}) {
  return (
    <section class="mb-6">
      <h3 class="flex items-center gap-2 text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">
        {title}
        <span class="font-normal">({questions.length})</span>
      </h3>
      <div class="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border">
        {questions.map((q) => (
          <QuestionRow
            key={q.id}
            question={q}
            orgId={orgId}
            isArchived={isArchived}
            onArchive={onArchive}
            onDelete={onDelete}
          />
        ))}
      </div>
    </section>
  );
}

function QuestionRow({
  question,
  orgId,
  isArchived,
  onArchive,
  onDelete,
}: {
  question: BankQuestion;
  orgId: string;
  isArchived?: boolean;
  onArchive: (q: BankQuestion, archived: boolean) => void;
  onDelete: (q: BankQuestion) => void;
}) {
  return (
    <div class="group flex items-center gap-4 px-5 py-4 hover:bg-surface-hover transition-colors">
      <a
        href={`/orgs/${orgId}/forms/bank/${question.id}`}
        class="flex items-center gap-4 min-w-0 flex-1"
      >
        <div
          class={`size-10 rounded-full flex items-center justify-center ring-2 flex-shrink-0 ${
            isArchived
              ? "bg-surface-muted text-text-muted ring-border"
              : "bg-[var(--brand-100)] text-[var(--brand-600)] ring-[var(--brand-200)]"
          }`}
        >
          {question.type === "rating" ? (
            <IcoStar class="size-5" />
          ) : (
            <span class="text-lg font-medium">Aa</span>
          )}
        </div>
        <div class="flex flex-col min-w-0 flex-1">
          <span
            class={`font-medium truncate ${
              isArchived
                ? "text-text-muted"
                : "text-text group-hover:text-[var(--brand-600)]"
            }`}
          >
            {question.title}
          </span>
          <span class="text-sm text-text-muted">
            {question.type === "rating"
              ? `Rating 1-${question.ratingMax}`
              : "Short answer"}
            {question.usageCount > 0 && (
              <span>
                {" "}
                &middot; Used {question.usageCount}{" "}
                {question.usageCount === 1 ? "time" : "times"}
              </span>
            )}
          </span>
        </div>
        <IcoChevronRight class="size-5 text-text-muted group-hover:text-[var(--brand-500)] transition-colors flex-shrink-0" />
      </a>

      {/* Actions */}
      <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onArchive(question, !question.archivedAt);
          }}
          class="p-2 rounded-lg text-text-muted hover:text-text hover:bg-surface-muted transition-colors"
          title={question.archivedAt ? "Restore" : "Archive"}
        >
          <IcoArchive class="size-4" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(question);
          }}
          class="p-2 rounded-lg text-text-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
          title="Delete"
        >
          <IcoTrash class="size-4" />
        </button>
      </div>
    </div>
  );
}
